import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FaChevronRight, FaChevronDown } from "react-icons/fa";

// Dark-theme collapsible JSON tree (matches the raw_text/llm_result viewer palette).
// Read-only: each object/array node can be collapsed per key/group for readability.

const INDENT = 14;

const valueClass = (v) => {
  if (typeof v === "string") return "tw-text-emerald-300";
  if (typeof v === "number") return "tw-text-amber-300";
  if (typeof v === "boolean") return "tw-text-fuchsia-300";
  if (v === null) return "tw-text-slate-500";
  return "tw-text-slate-100";
};

const renderPrimitive = (v) => {
  if (typeof v === "string") return `"${v}"`;
  if (v === null) return "null";
  return String(v);
};

const JsonNode = ({ name, value, depth, isArrayIndex, trailingComma, defaultOpenDepth }) => {
  const isContainer = value !== null && typeof value === "object";
  const [open, setOpen] = useState(depth < defaultOpenDepth);

  const pad = { paddingLeft: depth === 0 ? 0 : INDENT };
  const nameEl = name !== undefined ? <span className="tw-text-sky-300">{isArrayIndex ? name : `"${name}"`}</span> : null;
  const colon = name !== undefined ? <span className="tw-text-slate-400">: </span> : null;
  const comma = trailingComma ? <span className="tw-text-slate-400">,</span> : null;

  if (!isContainer) {
    return (
      <div style={pad} className="tw-whitespace-pre">
        {nameEl}{colon}<span className={valueClass(value)}>{renderPrimitive(value)}</span>{comma}
      </div>
    );
  }

  const isArray = Array.isArray(value);
  const entries = isArray ? value.map((v, i) => [i, v]) : Object.entries(value);
  const openBrace = isArray ? "[" : "{";
  const closeBrace = isArray ? "]" : "}";

  if (entries.length === 0) {
    return (
      <div style={pad} className="tw-whitespace-pre">
        {nameEl}{colon}<span className="tw-text-slate-400">{openBrace}{closeBrace}</span>{comma}
      </div>
    );
  }

  return (
    <div style={pad}>
      <div className="tw-flex tw-items-start tw-cursor-pointer hover:tw-bg-slate-800 tw-rounded-sm" onClick={() => setOpen((o) => !o)}>
        <span className="tw-text-slate-500 tw-mr-1 tw-mt-[3px] tw-shrink-0">{open ? <FaChevronDown size={9} /> : <FaChevronRight size={9} />}</span>
        <span className="tw-whitespace-pre">
          {nameEl}{colon}<span className="tw-text-slate-400">{openBrace}</span>
          {!open && (
            <>
              <span className="tw-text-slate-600">{isArray ? ` ${entries.length} items ` : ` ${entries.length} keys `}</span>
              <span className="tw-text-slate-400">{closeBrace}</span>{comma}
            </>
          )}
        </span>
      </div>
      {open && (
        <>
          {entries.map(([key, val], idx) => (
            <JsonNode
              key={key}
              name={key}
              value={val}
              depth={depth + 1}
              isArrayIndex={isArray}
              trailingComma={idx < entries.length - 1}
              defaultOpenDepth={defaultOpenDepth}
            />
          ))}
          <div style={{ paddingLeft: INDENT }} className="tw-text-slate-400 tw-whitespace-pre">{closeBrace}{comma}</div>
        </>
      )}
    </div>
  );
};

JsonNode.propTypes = {
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.any,
  depth: PropTypes.number,
  isArrayIndex: PropTypes.bool,
  trailingComma: PropTypes.bool,
  defaultOpenDepth: PropTypes.number,
};

const JsonTree = ({ data, height, initialOpenDepth = 2 }) => {
  const [epoch, setEpoch] = useState(0);
  const [openDepth, setOpenDepth] = useState(initialOpenDepth);

  // Node open-state is seeded from defaultOpenDepth at mount only; bump `epoch` to
  // remount the whole tree so expand/collapse-all re-seeds every node.
  const expandAll = () => { setOpenDepth(Infinity); setEpoch((e) => e + 1); };
  const collapseAll = () => { setOpenDepth(1); setEpoch((e) => e + 1); };

  const bodyStyle = useMemo(() => ({
    fontSize: 12.5,
    lineHeight: "20px",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  }), []);

  return (
    <div className="tw-flex tw-flex-col tw-rounded-sm tw-border tw-border-solid tw-border-slate-700 tw-overflow-hidden tw-bg-slate-900" style={{ height }}>
      <div className="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-1.5 tw-shrink-0 tw-border-b tw-border-solid tw-border-slate-700 tw-bg-slate-800">
        <button type="button" onClick={expandAll} className="tw-text-[11px] tw-text-sky-300 hover:tw-underline tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0">Expand all</button>
        <button type="button" onClick={collapseAll} className="tw-text-[11px] tw-text-sky-300 hover:tw-underline tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0">Collapse all</button>
      </div>
      <div className="tw-flex-1 tw-overflow-auto tw-px-3 tw-py-2 tw-text-slate-100" style={bodyStyle}>
        <JsonNode key={epoch} value={data} depth={0} trailingComma={false} defaultOpenDepth={openDepth} />
      </div>
    </div>
  );
};

JsonTree.propTypes = {
  data: PropTypes.any,
  height: PropTypes.string,
  initialOpenDepth: PropTypes.number,
};

export default JsonTree;
